import { useState, useEffect, useRef } from 'react';
import { AdminShell } from '@/components/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Upload, Trash2, Copy, Image as ImageIcon } from 'lucide-react';
import { getToken } from '@/lib/auth';
import api, { API_BASE } from '@/lib/api';
import { toast } from 'sonner';

export default function AdminMedia() {
  const [media, setMedia] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const fileInputRef = useRef(null);

  const fetchMedia = () => {
    api.get('/admin/media?limit=100').then(res => {
      setMedia(res.data.items || []);
      setTotal(res.data.total || 0);
    }).catch(console.error);
  };

  useEffect(() => { fetchMedia(); }, []);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    let uploaded = 0;
    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 5MB limit`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const token = getToken();
        await fetch(`${API_BASE}/admin/media/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        uploaded++;
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploaded > 0) {
      toast.success(`${uploaded} image${uploaded > 1 ? 's' : ''} uploaded.`);
      fetchMedia();
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this image?')) return;
    try {
      await api.delete(`/admin/media/${id}`);
      toast.success('Image deleted.');
      fetchMedia();
      if (selectedImage?._id === id) setSelectedImage(null);
    } catch (err) {
      toast.error('Failed to delete.');
    }
  };

  const copyUrl = (item) => {
    const fullUrl = `${window.location.origin}/api/media/${item._id}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast.success('Image URL copied to clipboard.');
    }).catch(() => {
      toast.error('Failed to copy URL.');
    });
  };

  const copyMarkdown = (item) => {
    const fullUrl = `${window.location.origin}/api/media/${item._id}`;
    const md = `![${item.alt_text || item.filename}](${fullUrl})`;
    navigator.clipboard.writeText(md).then(() => {
      toast.success('Markdown copied to clipboard.');
    }).catch(() => {
      toast.error('Failed to copy.');
    });
  };

  const handleAltTextUpdate = async (id, altText) => {
    try {
      await api.put(`/admin/media/${id}`, { alt_text: altText });
      fetchMedia();
    } catch (err) {
      toast.error('Failed to update alt text.');
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <AdminShell title="Media Library">
      <div className="flex items-center justify-between mb-6">
        <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">
          {total} image{total !== 1 ? 's' : ''}
        </p>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleUpload}
            className="hidden"
            id="media-upload"
          />
          <Button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm"
            data-testid="admin-media-upload-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Uploading...' : 'Upload Images'}
          </Button>
        </div>
      </div>

      {/* Grid */}
      {media.length === 0 ? (
        <div className="text-center py-20">
          <ImageIcon className="h-12 w-12 text-[hsl(var(--muted-foreground))] mx-auto mb-4" />
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))]">No images uploaded yet.</p>
          <p className="font-['Inter'] text-xs text-[hsl(var(--silver))] mt-2">Upload images to use in essays, pages, and products.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {media.map(item => (
            <div
              key={item._id}
              className="group relative rounded-xl overflow-hidden bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] hover:border-[rgba(201,168,76,0.35)] transition-colors cursor-pointer"
              onClick={() => setSelectedImage(item)}
            >
              <div className="aspect-square">
                <img
                  src={`/api/media/${item._id}`}
                  alt={item.alt_text || item.filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[rgba(7,10,10,0.8)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
                <p className="font-['Inter'] text-xs text-[hsl(var(--foreground))] truncate">{item.filename}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={(v) => !v && setSelectedImage(null)}>
        <DialogContent className="bg-[hsl(166,24%,11%)] border-[rgba(201,168,76,0.2)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))]">
              {selectedImage?.filename}
            </DialogTitle>
          </DialogHeader>
          {selectedImage && (
            <div className="space-y-4 mt-4">
              <div className="rounded-lg overflow-hidden border border-[rgba(201,168,76,0.12)] max-h-80 flex items-center justify-center bg-[rgba(7,10,10,0.5)]">
                <img
                  src={`/api/media/${selectedImage._id}`}
                  alt={selectedImage.alt_text || selectedImage.filename}
                  className="max-h-80 object-contain"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
                    Size: {formatSize(selectedImage.size)}
                  </p>
                  <p className="font-['Inter'] text-xs text-[hsl(var(--silver))]">
                    Type: {selectedImage.content_type}
                  </p>
                </div>
              </div>
              <div>
                <label className="font-['Inter'] text-xs font-medium uppercase tracking-[0.12em] text-[hsl(var(--silver))] mb-2 block">Alt Text</label>
                <Input
                  defaultValue={selectedImage.alt_text || ''}
                  onBlur={(e) => handleAltTextUpdate(selectedImage._id, e.target.value)}
                  placeholder="Describe the image..."
                  className="bg-[rgba(7,10,10,0.5)] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] font-['Inter'] text-sm"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyUrl(selectedImage)}
                  className="border-[rgba(201,168,76,0.2)] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] font-['Inter'] text-xs"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy URL
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyMarkdown(selectedImage)}
                  className="border-[rgba(201,168,76,0.2)] text-[hsl(var(--silver))] hover:text-[hsl(var(--foreground))] font-['Inter'] text-xs"
                >
                  <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy Markdown
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(selectedImage._id)}
                  className="border-[rgba(185,105,122,0.3)] text-[hsl(var(--rose))] hover:text-red-400 font-['Inter'] text-xs ml-auto"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminShell>
  );
}
